import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';
import { SHEET_TOKENS, getGlassSurface, getTextColors, getBackdropStyle, getHandleStyle } from './sheetStyle';

/**
 * Sjednocený bottom sheet pro cockpit Moje směna. Slide-up s drag handle,
 * swipe-down close (mobile), backdrop click close (desktop), Esc close.
 * Safe-area: respektuje notch/Dynamic Island i home indicator.
 * Glass: vícevrstvý povrch s backdrop blur + saturate (viz sheetStyle.js).
 */
export default function BottomSheet({
    open,
    title,
    onClose,
    isDark,
    isMobile,
    children,
    footer = null,             // nescrollovací footer (akční tlačítka apod.)
    fullscreen = false,        // mobile fullscreen — překryje celý displej včetně bottom-navu
    maxWidth = 520,            // desktop: centrovaný modal s touto šířkou
    hideClose = false,         // skryje zavírací X (sheet se zavírá jen tlačítkem)
}) {
    const sheetRef = useRef(null);
    const [dragY, setDragY] = useState(0);
    const dragStartY = useRef(null);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    // Tokens — viz sheetStyle.js. Glass povrch (multi-layer + backdrop blur+saturate)
    // se aplikuje jen na non-fullscreen sheety; fullscreen pokrývá celý viewport solidně.
    const glass = getGlassSurface(isDark);
    const colors = getTextColors(isDark);
    const labelMuted = colors.labelMuted;
    const textPrimary = colors.textPrimary;
    const cardBorder = colors.divider;

    const handleTouchStart = (e) => { dragStartY.current = e.touches[0].clientY; };
    const handleTouchMove = (e) => {
        if (dragStartY.current == null) return;
        const dy = e.touches[0].clientY - dragStartY.current;
        if (dy > 0) setDragY(dy);
    };
    const handleTouchEnd = () => {
        if (dragY > 80) onClose?.();
        setDragY(0);
        dragStartY.current = null;
    };

    // Fullscreen mode pokrývá celý viewport bez ohledu na mobile/desktop —
    // pro kritické akce (uzávěrka apod.) kde rozvozce nemá mít distraction
    // okolí a footer s primary tlačítkem musí být pinned na spodku viewportu.
    // Předtím `fullscreen=true` fungoval pouze na mobile; na desktopu sheet
    // auto-sizoval podle obsahu → tlačítka skončila vprostřed obrazovky.
    const mobileFullscreen = fullscreen;

    // Portal do document.body — sheet musí uniknout stacking-context trapu rodiče
    // (cockpit má backdrop-filter, transformy → fixed děti jsou uvězněné v rodičově
    // stacking contextu a hlavička aplikace (z-30) je překrývá).
    return createPortal((
        <div role="dialog" aria-modal="true"
            onClick={onClose}
            style={{
                ...getBackdropStyle(),
                background: mobileFullscreen ? glass.background : SHEET_TOKENS.backdrop.background,
                backdropFilter: mobileFullscreen ? glass.backdropFilter : SHEET_TOKENS.backdrop.backdropFilter,
                WebkitBackdropFilter: mobileFullscreen ? glass.backdropFilter : SHEET_TOKENS.backdrop.backdropFilter,
                // Sheety jsou vždy přilepené k dolnímu okraji viewportu — i na
                // desktopu (předtím byly centrované jako modaly). Sjednocuje to
                // chování napříč zařízeními a respektuje očekávání „bottom sheet".
                justifyContent: 'flex-end',
                alignItems: 'center',
                padding: 0,
                animation: 'msm-fadein 0.18s ease-out',
            }}>
            <style>{`
                @keyframes msm-fadein { from { opacity: 0 } to { opacity: 1 } }
                @keyframes msm-slideup { from { transform: translateY(100%); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
            `}</style>
            <div
                ref={sheetRef}
                onClick={e => e.stopPropagation()}
                onTouchStart={(isMobile && !mobileFullscreen) ? handleTouchStart : undefined}
                onTouchMove={(isMobile && !mobileFullscreen) ? handleTouchMove : undefined}
                onTouchEnd={(isMobile && !mobileFullscreen) ? handleTouchEnd : undefined}
                style={{
                    width: '100%',
                    maxWidth: isMobile ? '100%' : `min(${maxWidth}px, 92vw)`,
                    minHeight: mobileFullscreen ? '100dvh' : (isMobile ? '50vh' : 'auto'),
                    maxHeight: mobileFullscreen ? '100dvh' : (isMobile ? '85vh' : '90vh'),
                    // Glass povrch — multi-layer; fullscreen má jen background bez okraje
                    background: glass.background,
                    backdropFilter: glass.backdropFilter,
                    WebkitBackdropFilter: glass.backdropFilter,
                    border: mobileFullscreen ? 'none' : glass.border,
                    // Top-only rounded vždy (= bottom sheet vizuál) — desktop už
                    // nepoužívá all-around rounded modal styl, sheet je přilepený
                    // k dolní hraně viewportu.
                    borderRadius: mobileFullscreen
                        ? 0
                        : `${SHEET_TOKENS.radius.container}px ${SHEET_TOKENS.radius.container}px 0 0`,
                    boxShadow: mobileFullscreen ? 'none' : glass.boxShadow,
                    color: glass.color,
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    transform: mobileFullscreen ? 'none' : `translateY(${dragY}px)`,
                    transition: dragY === 0 ? 'transform 0.2s ease' : 'none',
                    paddingTop: mobileFullscreen
                        ? 'calc(env(safe-area-inset-top, 0px) * 0.9 + 8px)'
                        : 0,
                    paddingBottom: (isMobile || mobileFullscreen) ? 'env(safe-area-inset-bottom, 0px)' : 0,
                    animation: 'msm-slideup 0.22s ease-out',
                }}>
                {/* Drag handle — jen v non-fullscreen mobile (fullscreen má X tlačítko) */}
                {isMobile && !mobileFullscreen && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
                        <span style={getHandleStyle(isDark)} />
                    </div>
                )}

                {/* Fullscreen close X — pravý horní roh, nad safe-area top */}
                {mobileFullscreen && !hideClose && (
                    <button type="button" onClick={onClose}
                        aria-label="Zavřít"
                        style={{
                            position: 'absolute',
                            top: 'calc(env(safe-area-inset-top, 0px) * 0.9 + 10px)',
                            right: 12,
                            width: SHEET_TOKENS.sizes.closeBtn, height: SHEET_TOKENS.sizes.closeBtn,
                            borderRadius: '50%',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                            border: `1px solid ${cardBorder}`,
                            color: textPrimary, cursor: 'pointer',
                            zIndex: 5,
                        }}>
                        <X size={18} />
                    </button>
                )}

                {/* Header — title + close X. Padding sjednoceně přes všechny sheety. */}
                {title != null && (
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: isMobile
                            ? `4px ${SHEET_TOKENS.spacing.sheetPadX}px 10px`
                            : `${SHEET_TOKENS.spacing.headerPadY}px ${SHEET_TOKENS.spacing.sheetPadX + 2}px 10px`,
                        borderBottom: `1px solid ${cardBorder}`,
                        flexShrink: 0,
                    }}>
                        <span style={{
                            fontSize: SHEET_TOKENS.typography.title.size,
                            fontWeight: SHEET_TOKENS.typography.title.weight,
                            letterSpacing: SHEET_TOKENS.typography.title.letterSpacing,
                            color: textPrimary,
                        }}>{title}</span>
                        {!hideClose && (
                            <button type="button" onClick={onClose}
                                aria-label="Zavřít"
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: labelMuted, padding: 4, lineHeight: 0,
                                }}>
                                <ChevronDown size={20} />
                            </button>
                        )}
                    </div>
                )}

                {/* Content scroll */}
                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0, minWidth: 0 }}>
                    {children}
                </div>

                {/* Footer (nescrolluje, vždy viditelný) */}
                {footer && (
                    <div style={{
                        flexShrink: 0,
                        padding: `12px ${SHEET_TOKENS.spacing.sheetPadX + 2}px 14px`,
                        borderTop: `1px solid ${cardBorder}`,
                        // Footer dědí glass z parent kontejneru — bez vlastního pozadí.
                        background: 'transparent',
                    }}>
                        {footer}
                    </div>
                )}
            </div>
        </div>
    ), document.body);
}
